import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import { searchMember } from '../../shared/api/memberApi';
import type { MemberSearchData } from '../../shared/dto/EntityRepository';
import { getLandingMessage } from '../../shared/constants/pageMessages';
import { useModalStore } from '../../shared/store/portal/modalStore';

const LandingSearchModal: React.FC = () => {
  const clearModal = useModalStore((s) => s.clearModal);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberSearchData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(0);
  const latestSearchId = useRef(0);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearModal]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const searchId = ++latestSearchId.current;

    if (!normalizedQuery) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }

    setIsSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchMember(normalizedQuery);
        if (latestSearchId.current !== searchId) return;
        setResults(response.data.members ?? []);
      } catch {
        if (latestSearchId.current !== searchId) return;
        setResults([]);
        setSearchFailed(true);
      } finally {
        if (latestSearchId.current === searchId) setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (latestSearchId.current === searchId) latestSearchId.current += 1;
    };
  }, [query]);

  useEffect(() => {
    setSelectedCandidate(0);
  }, [results]);

  const openMemberArchive = (member: MemberSearchData) => {
    if (!member.handle?.trim()) return;
    clearModal();
    navigate(`/@${member.handle}/`);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedCandidate((prev) => (prev > 0 ? prev - 1 : prev));
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedCandidate((prev) => (prev < results.length - 1 ? prev + 1 : prev));
      return;
    }
    if (event.key === 'Enter') {
      const selected = results[selectedCandidate];
      if (!selected) return;
      openMemberArchive(selected);
    }
  };

  return (
    <Container>
      <Header>
        <Title>{getLandingMessage('LANDING_SEARCH_TITLE')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => clearModal()}
          aria-label="Close"
        />
      </Header>
      <SearchInput
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={getLandingMessage('LANDING_SEARCH_PLACEHOLDER')}
        aria-label={getLandingMessage('LANDING_SEARCH_PLACEHOLDER')}
        autoFocus
      />
      <SearchResults>
        {isSearching && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_SEARCHING')}</SearchMessage>
        )}
        {!isSearching && searchFailed && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_FAILED')}</SearchMessage>
        )}
        {!isSearching && !searchFailed && !query.trim() && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_EMPTY')}</SearchMessage>
        )}
        {!isSearching && !searchFailed && query.trim() && results.length === 0 && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_NO_RESULTS')}</SearchMessage>
        )}
        {!isSearching &&
          results.map((member, index) => (
            <SearchResult
              key={member.aui}
              type="button"
              $isSelected={index === selectedCandidate}
              onClick={() => openMemberArchive(member)}
              onMouseEnter={() => setSelectedCandidate(index)}
            >
              <ResultName>{member.name?.trim() || member.username}</ResultName>
              <ResultHandle>@{member.handle}</ResultHandle>
            </SearchResult>
          ))}
      </SearchResults>
    </Container>
  );
};

export default LandingSearchModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 17px 14px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 6px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  outline: none;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const SearchResults = styled.div`
  min-height: 220px;
  max-height: 50vh;
  overflow-y: auto;
`;

const SearchResult = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  padding: 10px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.color_Gray_07 : 'transparent'};
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  cursor: pointer;
  text-align: left;
  ${({ theme }) => theme.typography.Body_04};

  &:hover {
    background-color: ${({ theme }) => theme.colors.color_Gray_07};
  }
`;

const ResultName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResultHandle = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const SearchMessage = styled.div`
  min-height: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-align: center;
  ${({ theme }) => theme.typography.Body_04};
`;
